import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import { useAuth } from "../../../auth/AuthContext";

/**
 * 1-on-1s — invite a class cohort to book start-of-year meetings with Dr. Li,
 * and watch the bookings fill in.
 *
 * The send flow is the shape CLAUDE.md requires for anything that emails
 * families: preview first, a test to yourself, then a button that names the
 * exact number of families. That number is sent to the server as
 * confirm_count and the server refuses if it no longer matches — so a stale
 * page or the wrong cohort cannot email a count nobody looked at.
 *
 * Calls the edge function with a plain fetch rather than functions.invoke:
 * invoke drops the response body on a non-2xx, and the refusal messages here
 * ("confirmation did not match: about to email 13") are the point.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";
const CAMPAIGN = "fall-2026-1on1";
const TZ = "America/Los_Angeles";

interface ClassOpt {
  class_id: string;
  name: string;
  status: string;
}
interface PStudent {
  student_id: string;
  name: string;
  student_email: string | null;
  guardians: string[];
  booked: boolean;
}

/* The three summer cohorts this campaign invited. Held as ids, not names:
   "A210A03 - Returning Student" does not match the pattern the other two do,
   and a class rename would silently empty a name-matched list rather than
   failing where someone would notice. The test class is deliberately absent. */
const SUMMER_CLASS_IDS = [
  "4ee59ab4-8ea1-4ffa-a276-d18a0e62bbb9", // A210A01 - Ardent Scholar
  "cb3f9e34-360d-4501-bd5b-5dc0f9f83c84", // A210A02 - Ardent Scholar
  "a705c426-e265-46dd-bfcf-5297a0834a73", // A210A03 - Returning Student
];

interface UnbookedStudent {
  student_id: string;
  name: string;
  email: string | null;
  className: string;
  step: number | null;
  project_id: string | null;
}
interface Preview {
  class: string;
  student_count: number;
  recipient_count: number;
  students: PStudent[];
  sample_subject: string | null;
  sample_text: string | null;
}
interface SlotView {
  slot_id: string;
  starts_at: string;
  who: string | null;
  blocked: string | null;
  /* The booked student's active project, so their name in the grid opens the
     work you are about to meet them about. Null when they have none yet — a
     family can book before a project exists, and a dead link is worse than
     plain text. */
  project_id: string | null;
}

const dayFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  weekday: "short",
  month: "short",
  day: "numeric",
});
const timeFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  hour: "numeric",
  minute: "2-digit",
});
/* "2026-09-17" — the Pacific calendar day a reminder is sent for. */
const dayKeyFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export default function OneOnOnesPage() {
  const { session } = useAuth();

  const [classes, setClasses] = useState<ClassOpt[]>([]);
  const [classId, setClassId] = useState("");
  const [unbooked, setUnbooked] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState<null | "preview" | "test" | "send">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [slots, setSlots] = useState<SlotView[]>([]);
  const [slotsErr, setSlotsErr] = useState<string | null>(null);
  /* Named apart from `unbooked` above, which is the send flow's checkbox
     ("only email families who have not booked"). Two different things. */
  const [unbookedStudents, setUnbookedStudents] = useState<UnbookedStudent[]>(
    [],
  );
  const [unbookedStudentsErr, setUnbookedStudentsErr] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();
  const [reminding, setReminding] = useState<{
    key: string;
    day: string;
  } | null>(null);

  async function call(
    body: Record<string, unknown>,
    fn = "send-year-start-invites",
  ) {
    /* Fresh session at call time, not the one this page rendered with — an
       expired token gets a bare 401 from the functions gateway. getSession()
       refreshes it first. */
    const {
      data: { session: fresh },
    } = await supabase.auth.getSession();
    const res = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${fresh?.access_token ?? session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ campaign: CAMPAIGN, ...body }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401 && !data?.error)
        throw new Error(
          "Your sign-in has expired. Refresh the page (sign in again if asked) and try once more.",
        );
      throw new Error(
        data?.error ??
          data?.message ??
          data?.msg ??
          `Request failed (${res.status})`,
      );
    }
    return data;
  }

  // ── classes ─────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase
      .from("class")
      .select("class_id, name, status")
      .in("status", ["active", "upcoming"])
      .order("name")
      .then(({ data, error: e }) => {
        if (e) {
          setError(e.message);
          return;
        }
        setClasses((data ?? []) as ClassOpt[]);
      });
  }, []);

  // ── bookings grid ───────────────────────────────────────────────────────
  async function loadSlots() {
    setSlotsErr(null);
    const { data: rows, error: e } = await supabase
      .from("meeting_slot")
      .select("slot_id, starts_at, meeting_id, blocked_label")
      .eq("campaign", CAMPAIGN)
      .order("starts_at");
    if (e) {
      setSlotsErr(e.message);
      return;
    }

    const meetingIds = (rows ?? [])
      .map((r) => r.meeting_id)
      .filter(Boolean) as string[];
    const nameByMeeting: Record<string, string> = {};
    const projectByMeeting: Record<string, string> = {};
    if (meetingIds.length > 0) {
      const { data: ms, error: mErr } = await supabase
        .from("meeting")
        .select("meeting_id, student_id")
        .in("meeting_id", meetingIds);
      if (mErr) {
        setSlotsErr(mErr.message);
        return;
      }
      const sids = (ms ?? []).map((m) => m.student_id);
      const { data: ps } = await supabase
        .from("student_profile")
        .select("student_id, user_id")
        .in("student_id", sids);
      const { data: us } = await supabase
        .from("users")
        .select("user_id, first_name, last_name")
        .in(
          "user_id",
          (ps ?? []).map((p) => p.user_id),
        );
      const userOf = Object.fromEntries(
        (ps ?? []).map((p) => [p.student_id, p.user_id]),
      );
      const nameOf = Object.fromEntries(
        (us ?? []).map((u) => [
          u.user_id,
          `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim(),
        ]),
      );
      /* Their active project, for the link behind the name. A student has at
         most one, so the first is the one. */
      const { data: prs } = await supabase
        .from("project")
        .select("project_id, student_id")
        .in("student_id", sids)
        .eq("status", "active");
      const projectOf = Object.fromEntries(
        (prs ?? []).map((p) => [p.student_id, p.project_id]),
      );

      for (const m of ms ?? []) {
        nameByMeeting[m.meeting_id] = nameOf[userOf[m.student_id]] || "Booked";
        const pid = projectOf[m.student_id];
        if (pid) projectByMeeting[m.meeting_id] = pid;
      }
    }
    setSlots(
      (rows ?? []).map((r) => ({
        slot_id: r.slot_id,
        starts_at: r.starts_at,
        who: r.meeting_id ? (nameByMeeting[r.meeting_id] ?? "Booked") : null,
        blocked: r.blocked_label ?? null,
        project_id: r.meeting_id
          ? (projectByMeeting[r.meeting_id] ?? null)
          : null,
      })),
    );
  }
  useEffect(() => {
    loadSlots();
  }, []);

  /* Who still owes us a booking. The grid answers "which times are taken";
     this answers "who do I chase", which is the question that actually gets
     someone on the calendar.

     Unbooked means no SCHEDULED meeting — a student whose booking was
     cancelled has not got one, and counting their cancelled row as a booking
     would quietly drop them off this list. */
  async function loadUnbooked() {
    const { data: enrolls, error: eErr } = await supabase
      .from("class_enrollment")
      .select("student_id, class_id")
      .in("class_id", SUMMER_CLASS_IDS)
      .eq("status", "active");
    if (eErr) {
      setUnbookedStudentsErr(eErr.message);
      return;
    }
    const studentIds = (enrolls ?? []).map((e) => e.student_id);
    if (studentIds.length === 0) {
      setUnbookedStudents([]);
      return;
    }

    const [{ data: booked }, { data: cls }, { data: profs }, { data: projs }] =
      await Promise.all([
        supabase
          .from("meeting")
          .select("student_id")
          .eq("kind", "year_start")
          /* held counts as booked. A student who has already had their
             meeting is not someone to chase — filtering to 'scheduled'
             alone empties this list into "nobody booked" the moment a
             wave is marked held. */
          .in("status", ["scheduled", "held"])
          .in("student_id", studentIds),
        supabase
          .from("class")
          .select("class_id, name")
          .in("class_id", SUMMER_CLASS_IDS),
        supabase
          .from("student_profile")
          .select("student_id, user_id")
          .in("student_id", studentIds),
        supabase
          .from("project")
          .select("project_id, student_id, current_step")
          .in("student_id", studentIds)
          .eq("status", "active"),
      ]);

    const { data: us } = await supabase
      .from("users")
      .select("user_id, first_name, last_name, email")
      .in(
        "user_id",
        (profs ?? []).map((p) => p.user_id),
      );

    const bookedSet = new Set((booked ?? []).map((m) => m.student_id));
    const classOf = Object.fromEntries(
      (cls ?? []).map((c) => [c.class_id, c.name]),
    );
    const userOf = Object.fromEntries(
      (profs ?? []).map((p) => [p.student_id, p.user_id]),
    );
    const infoOf = Object.fromEntries(
      (us ?? []).map((u) => [
        u.user_id,
        {
          name: `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email,
          email: u.email as string | null,
        },
      ]),
    );
    const projOf = Object.fromEntries(
      (projs ?? []).map((p) => [
        p.student_id,
        { project_id: p.project_id, step: p.current_step as number | null },
      ]),
    );

    const out: UnbookedStudent[] = [];
    for (const e of enrolls ?? []) {
      if (bookedSet.has(e.student_id)) continue;
      const info = infoOf[userOf[e.student_id]];
      out.push({
        student_id: e.student_id,
        name: info?.name ?? "Unknown student",
        email: info?.email ?? null,
        className: classOf[e.class_id] ?? "—",
        step: projOf[e.student_id]?.step ?? null,
        project_id: projOf[e.student_id]?.project_id ?? null,
      });
    }
    out.sort(
      (a, b) =>
        a.className.localeCompare(b.className) || a.name.localeCompare(b.name),
    );
    setUnbookedStudents(out);
  }
  useEffect(() => {
    loadUnbooked();
    // Runs once: the roster is read on mount, and the send flow reloads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Grouped for the render: the useful question is per cohort, not pooled. */
  const unbookedByClass = useMemo(() => {
    const out: { className: string; students: UnbookedStudent[] }[] = [];
    for (const s of unbookedStudents) {
      const g = out.find((x) => x.className === s.className);
      if (g) g.students.push(s);
      else out.push({ className: s.className, students: [s] });
    }
    return out;
  }, [unbookedStudents]);

  const byDay = useMemo(() => {
    const out: { day: string; key: string; slots: SlotView[] }[] = [];
    for (const s of slots) {
      const d = dayFmt.format(new Date(s.starts_at));
      const g = out.find((x) => x.day === d);
      if (g) g.slots.push(s);
      else
        out.push({
          day: d,
          key: dayKeyFmt.format(new Date(s.starts_at)),
          slots: [s],
        });
    }
    return out;
  }, [slots]);

  const bookedCount = slots.filter((s) => s.who).length;
  const bookableCount = slots.filter((s) => !s.blocked).length;

  // ── actions ─────────────────────────────────────────────────────────────
  async function doPreview() {
    if (!classId) return;
    setBusy("preview");
    setError(null);
    setNotice(null);
    try {
      setPreview(
        await call({
          class_id: classId,
          mode: "preview",
          only_unbooked: unbooked,
        }),
      );
    } catch (e) {
      setError((e as Error).message);
      setPreview(null);
    } finally {
      setBusy(null);
    }
  }

  async function doTest() {
    setBusy("test");
    setError(null);
    setNotice(null);
    try {
      const r = await call({
        class_id: classId,
        mode: "test",
        only_unbooked: unbooked,
      });
      setNotice(
        `Test sent to ${r.sent_to}, written as if to ${r.as_student}'s family. Its button is live and books a real time for that student.`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function doSend() {
    if (!preview) return;
    setConfirm(false);
    setBusy("send");
    setError(null);
    setNotice(null);
    try {
      const r = await call({
        class_id: classId,
        mode: "send",
        only_unbooked: unbooked,
        confirm_count: preview.student_count,
      });
      setNotice(
        `Sent for ${r.sent} of ${r.students} students in ${r.class}.${r.failed ? ` ${r.failed} failed — see below.` : ""}`,
      );
      setPreview(null);
      await loadSlots();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const noun = unbooked ? "families who have not booked" : "families";

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
        1-on-1s
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start-of-year meetings with Dr. Li · 20 minutes · {bookedCount} of{" "}
        {bookableCount} times booked
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}

      {/* ── invite a cohort ──────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 4, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 2 }}>Invite a class</Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            select
            size="small"
            label="Class"
            value={classId}
            sx={{ minWidth: 280 }}
            onChange={(e) => {
              setClassId(e.target.value);
              setPreview(null);
            }}
          >
            {classes.map((c) => (
              <MenuItem key={c.class_id} value={c.class_id}>
                {c.name}
                {c.status === "upcoming" ? " (upcoming)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={unbooked}
                onChange={(e) => {
                  setUnbooked(e.target.checked);
                  setPreview(null);
                }}
              />
            }
            label="Only families who have not booked (reminder)"
          />
          <Button
            variant="outlined"
            disabled={!classId || busy !== null}
            onClick={doPreview}
          >
            {busy === "preview" ? "Loading…" : "Preview"}
          </Button>
        </Box>

        {preview && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>{preview.student_count}</strong> students ·{" "}
              <strong>{preview.recipient_count}</strong> email addresses in{" "}
              {preview.class}
            </Typography>

            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflowX: "auto",
                mb: 2,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Student email
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Guardian email
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Booked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.students.map((s) => (
                    <TableRow key={s.student_id}>
                      <TableCell>{s.name || "—"}</TableCell>
                      <TableCell>{s.student_email ?? "—"}</TableCell>
                      <TableCell>
                        {s.guardians.length ? (
                          s.guardians.join(", ")
                        ) : (
                          <Typography variant="body2" color="warning.main">
                            none on file
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.booked ? (
                          <Chip size="small" color="success" label="Booked" />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {preview.sample_subject && (
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}
              >
                <Typography variant="caption" color="text.secondary">
                  Email, as the first family will see it
                </Typography>
                <Typography sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
                  {preview.sample_subject}
                </Typography>
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0 }}
                >
                  {preview.sample_text}
                </Typography>
              </Paper>
            )}

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                disabled={busy !== null}
                onClick={doTest}
              >
                {busy === "test" ? "Sending…" : "Send test to me"}
              </Button>
              <Button
                variant="contained"
                disabled={busy !== null || preview.student_count === 0}
                onClick={() => setConfirm(true)}
              >
                {busy === "send"
                  ? "Sending…"
                  : `Send to ${preview.student_count} ${noun}`}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ── project sponsor email ────────────────────────────────────────── */}
      <SponsorNudgeCard classes={classes} call={call} />

      {/* ── bookings ─────────────────────────────────────────────────────── */}
      <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Bookings</Typography>
      {slotsErr && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {slotsErr}
        </Alert>
      )}
      {slots.length === 0 && !slotsErr ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          }}
        >
          {byDay.map((g) => (
            <Paper
              key={g.day}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                {g.day}
              </Typography>
              {g.slots.map((s) => (
                <Box
                  key={s.slot_id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    py: 0.5,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ width: 64, color: "text.secondary" }}
                  >
                    {timeFmt.format(new Date(s.starts_at))}
                  </Typography>
                  {s.who && s.project_id ? (
                    /* Straight to the work you are about to discuss with them. */
                    <Typography
                      variant="body2"
                      component="span"
                      role="link"
                      tabIndex={0}
                      title={`Open ${s.who}'s project`}
                      onClick={() => navigate(`/review/${s.project_id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/review/${s.project_id}`);
                        }
                      }}
                      sx={{
                        fontWeight: 600,
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textDecorationColor: "transparent",
                        transition: "text-decoration-color 120ms",
                        "&:hover, &:focus-visible": {
                          textDecorationColor: "currentColor",
                        },
                      }}
                    >
                      {s.who}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: s.who || s.blocked ? 600 : 400,
                        color: s.who
                          ? "text.primary"
                          : s.blocked
                            ? "warning.dark"
                            : "text.disabled",
                      }}
                    >
                      {s.who ?? s.blocked ?? "Open"}
                    </Typography>
                  )}
                </Box>
              ))}
              <Button
                size="small"
                variant="outlined"
                fullWidth
                sx={{ mt: 1.5 }}
                disabled={!g.slots.some((s) => s.who)}
                onClick={() => setReminding({ key: g.key, day: g.day })}
              >
                Send reminder
              </Button>
            </Paper>
          ))}
        </Box>
      )}

      {/* ── Who has not booked ──────────────────────────────────────────
          The grid above says which times are taken. This says who to chase. */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.25 }}>
          Not yet booked
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Active students in the summer cohorts with no scheduled 1-on-1 ·{" "}
          {unbookedStudents.length}{" "}
          {unbookedStudents.length === 1 ? "student" : "students"}
        </Typography>

        {unbookedStudentsErr && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {unbookedStudentsErr}
          </Alert>
        )}

        {unbookedStudents.length === 0 && !unbookedStudentsErr ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Everyone in these cohorts has booked.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            }}
          >
            {unbookedByClass.map((g) => (
              <Paper
                key={g.className}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2 }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {g.className}
                  </Typography>
                  <Chip
                    size="small"
                    color="warning"
                    label={g.students.length}
                  />
                </Box>
                {g.students.map((s) => (
                  <Box
                    key={s.student_id}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                      py: 0.5,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    {s.project_id ? (
                      /* Same as the grid: straight to the work. */
                      <Typography
                        variant="body2"
                        component="span"
                        role="link"
                        tabIndex={0}
                        title={`Open ${s.name}'s project`}
                        onClick={() => navigate(`/review/${s.project_id}`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/review/${s.project_id}`);
                          }
                        }}
                        sx={{
                          flex: 1,
                          fontWeight: 600,
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {s.name}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, fontWeight: 600 }}
                      >
                        {s.name}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {s.step != null ? `Step ${s.step}` : "no project"}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Send to {preview?.student_count} {noun}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This emails every student and guardian listed for{" "}
            <strong>{preview?.class}</strong> — {preview?.recipient_count}{" "}
            addresses. Each family gets its own booking link. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={doSend}>
            Send to {preview?.student_count} {noun}
          </Button>
        </DialogActions>
      </Dialog>

      {reminding && (
        <ReminderDialog
          day={reminding}
          call={call}
          onClose={() => setReminding(null)}
          onSent={(msg) => {
            setReminding(null);
            setNotice(msg);
            loadSlots();
          }}
        />
      )}
    </Box>
  );
}

/* "Ask a science teacher to be your project sponsor" — a soft push to one
   class. Preview, a test to yourself (both copies), then a confirmed send; the
   server refuses a count nobody saw. Students get one copy and each parent
   their own, because the parent's copy can carry a personal sign-in link. */
function SponsorNudgeCard({
  classes,
  call,
}: {
  classes: ClassOpt[];
  call: (body: Record<string, unknown>, fn?: string) => Promise<any>;
}) {
  const FN = "send-sponsor-nudge";
  const [classId, setClassId] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState<null | "preview" | "test" | "send">(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);

  async function run(mode: "preview" | "test" | "send") {
    setConfirm(false);
    setBusy(mode);
    setErr(null);
    setInfo(null);
    try {
      const r = await call(
        {
          class_id: classId,
          mode,
          ...(mode === "send" ? { confirm_count: preview?.student_count } : {}),
        },
        FN,
      );
      if (mode === "preview") setPreview(r);
      if (mode === "test")
        setInfo(
          `Test sent to ${r.sent_to}: ${r.count} email${r.count === 1 ? "" : "s"} (student and parent copies) written as if to ${r.as_student}'s family.`,
        );
      if (mode === "send") {
        setInfo(
          `Sent for ${r.sent} of ${r.students} students in ${r.class}.${r.failed ? ` ${r.failed} had an address that could not be emailed.` : ""}`,
        );
        setPreview(null);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const count: number = preview?.student_count ?? 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 4, borderRadius: 2 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
        Project sponsor email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ask each family to find a science teacher to sponsor the project, with
        the student's current step. Parents without an account get a link to
        create one; families who have not booked get the booking link.
      </Typography>

      <Box
        sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        <TextField
          select
          size="small"
          label="Class"
          value={classId}
          sx={{ minWidth: 280 }}
          onChange={(e) => {
            setClassId(e.target.value);
            setPreview(null);
            setInfo(null);
          }}
        >
          {classes.map((c) => (
            <MenuItem key={c.class_id} value={c.class_id}>
              {c.name}
              {c.status === "upcoming" ? " (upcoming)" : ""}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          disabled={!classId || busy !== null}
          onClick={() => run("preview")}
        >
          {busy === "preview" ? "Loading…" : "Preview"}
        </Button>
      </Box>

      {err && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {preview && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>{preview.student_count}</strong> students ·{" "}
            <strong>{preview.recipient_count}</strong> email addresses in{" "}
            {preview.class}
          </Typography>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflowX: "auto",
              mb: 2,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>1-on-1</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(preview.students ?? []).map(
                  (s: {
                    student_id: string;
                    name: string;
                    step: number | null;
                    step_name: string | null;
                    booked: boolean;
                    parents: { email: string; has_account: boolean }[];
                  }) => (
                    <TableRow key={s.student_id}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>
                        {s.step
                          ? `Step ${s.step}${s.step_name ? ` · ${s.step_name}` : ""}`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {s.booked ? (
                          <Chip size="small" color="success" label="Booked" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            gets booking link
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.parents.length === 0 ? (
                          <Typography variant="body2" color="warning.main">
                            none on file
                          </Typography>
                        ) : (
                          s.parents.map((p) => (
                            <Typography key={p.email} variant="body2">
                              {p.email}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                {p.has_account
                                  ? " · has account"
                                  : " · gets account link"}
                              </Typography>
                            </Typography>
                          ))
                        )}
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mb: 2,
            }}
          >
            {[
              { title: "Student's copy", sample: preview.student_sample },
              {
                title: `Parent's copy${preview.parent_sample?.for ? ` (${preview.parent_sample.for})` : ""}`,
                sample: preview.parent_sample,
              },
            ].map(
              (x) =>
                x.sample && (
                  <Paper
                    key={x.title}
                    variant="outlined"
                    sx={{ p: 2, bgcolor: "grey.50" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {x.title}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
                      {x.sample.subject}
                    </Typography>
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "inherit",
                        m: 0,
                      }}
                    >
                      {x.sample.text}
                    </Typography>
                  </Paper>
                ),
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              disabled={busy !== null || count === 0}
              onClick={() => run("test")}
            >
              {busy === "test" ? "Sending…" : "Send test to me"}
            </Button>
            <Button
              variant="contained"
              disabled={busy !== null || count === 0}
              onClick={() => setConfirm(true)}
            >
              {busy === "send" ? "Sending…" : `Send to ${count} families`}
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Send the sponsor email to {count} families?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This emails every student and parent listed for{" "}
            <strong>{preview?.class}</strong> — {preview?.recipient_count}{" "}
            addresses. Parents without an account get their own sign-in link.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => run("send")}>
            Send to {count} families
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/* Day-of reminder for everyone booked on one day. The dialog is the
   confirmation: it lists who will be emailed and the send button names the
   count, which the server checks against what it found. */
function ReminderDialog({
  day,
  call,
  onClose,
  onSent,
}: {
  day: { key: string; day: string };
  call: (body: Record<string, unknown>, fn?: string) => Promise<any>;
  onClose: () => void;
  onSent: (msg: string) => void;
}) {
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState<null | "load" | "test" | "send">("load");
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    call({ day: day.key, mode: "preview" }, "send-meeting-reminders")
      .then(setData)
      .catch((e) => setErr((e as Error).message))
      .finally(() => setBusy(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.key]);

  async function run(mode: "test" | "send") {
    setBusy(mode);
    setErr(null);
    setInfo(null);
    try {
      const r = await call(
        {
          day: day.key,
          mode,
          ...(mode === "send" ? { confirm_count: data?.count } : {}),
        },
        "send-meeting-reminders",
      );
      if (mode === "test")
        setInfo(
          `Test sent to ${r.sent_to}, written as if to ${r.as_student}'s family.`,
        );
      else
        onSent(
          `Reminders sent for ${r.sent} of ${r.bookings} bookings on ${day.day}.${r.failed ? ` ${r.failed} could not be sent.` : ""}`,
        );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const count: number = data?.count ?? 0;
  const reminded = (iso: string | null) =>
    iso ? ` · reminded ${timeFmt.format(new Date(iso))}` : "";

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Send reminders for {day.day}
      </DialogTitle>
      <DialogContent>
        {busy === "load" && (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {data && (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <DialogContentText sx={{ fontSize: 14 }}>
              Emails the student and parent for each booking below, with a
              button to choose a different time before the meeting starts.
            </DialogContentText>
            {(data.bookings ?? []).map(
              (b: {
                time: string;
                name: string;
                to: string[];
                reminder_sent_at: string | null;
                has_link: boolean;
              }) => (
                <Box
                  key={`${b.time}-${b.name}`}
                  sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {b.time} · {b.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {b.to.length ? b.to.join(", ") : "no email on file"}
                    {reminded(b.reminder_sent_at)}
                    {b.has_link ? "" : " · no booking link on file"}
                  </Typography>
                </Box>
              ),
            )}
            {data.sample_subject && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="caption" color="text.secondary">
                  Email, as the first family will see it
                </Typography>
                <Typography sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
                  {data.sample_subject}
                </Typography>
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0 }}
                >
                  {data.sample_text}
                </Typography>
              </Paper>
            )}
          </Box>
        )}
        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {info}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          disabled={!!busy || count === 0}
          onClick={() => run("test")}
        >
          {busy === "test" ? "Sending…" : "Send test to me"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={!!busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!!busy || count === 0}
          onClick={() => run("send")}
        >
          {busy === "send"
            ? "Sending…"
            : `Send reminder to ${count} ${count === 1 ? "family" : "families"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
